import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  User, 
  Mail, 
  Phone, 
  Building, 
  MapPin, 
  Calendar,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  XCircle,
  Download
} from 'lucide-react';
import Button from '../common/Button';
import { testData } from '../../data/testData';

interface UserDetailsModalProps {
  user: any;
  onClose: () => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ user, onClose }) => {
  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'Ready': return <TrendingUp className="w-4 h-4 text-success-600" />;
      case 'Almost Ready': return <AlertTriangle className="w-4 h-4 text-warning-600" />;
      case 'Not Ready': return <XCircle className="w-4 h-4 text-error-600" />;
      default: return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'Ready': return 'bg-success-100 text-success-700 border-success-200';
      case 'Almost Ready': return 'bg-warning-100 text-warning-700 border-warning-200';
      case 'Not Ready': return 'bg-error-100 text-error-700 border-error-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const handleExportUserData = () => {
    const userData = {
      personalInfo: {
        fullName: user.fullName,
        email: user.email,
        phone: user.phone,
        businessType: user.businessType,
        role: user.role,
        location: `${user.state}, ${user.country}`,
        registrationDate: new Date(user.createdAt).toLocaleDateString()
      },
      testResults: user.results ? {
        overallScore: `${user.results.overallPercentage}%`,
        status: user.results.overallStatus,
        completionDate: new Date(user.results.completedAt).toLocaleDateString(),
        categoryBreakdown: user.results.categories?.map((cat: any) => ({
          category: testData.find(c => c.id === cat.categoryId)?.title || cat.categoryId,
          score: `${cat.percentage}%`,
          status: cat.status
        })) || []
      } : null
    };

    const dataStr = JSON.stringify(userData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${user.fullName.replace(/\s+/g, '_')}_export_readiness_data.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-6 text-white">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold mb-2">{user.fullName}</h2>
                <p className="opacity-90">{user.role} at {user.businessType}</p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<Download />}
                  onClick={handleExportUserData}
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                >
                  Export Data
                </Button>
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Personal Information */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-primary-600" />
                    Personal Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <div>
                        <div className="text-sm text-gray-500">Email</div>
                        <div className="font-medium">{user.email}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <div>
                        <div className="text-sm text-gray-500">Phone</div>
                        <div className="font-medium">{user.phone}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Building className="w-4 h-4 text-gray-500" />
                      <div>
                        <div className="text-sm text-gray-500">Business</div>
                        <div className="font-medium">{user.businessType}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <div>
                        <div className="text-sm text-gray-500">Location</div>
                        <div className="font-medium">{user.state}, {user.country}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <div>
                        <div className="text-sm text-gray-500">Registration Date</div>
                        <div className="font-medium">
                          {new Date(user.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Test Results */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary-600" />
                    Test Results
                  </h3>
                  
                  {user.results ? (
                    <div className="space-y-4">
                      {/* Overall Score */}
                      <div className="bg-gradient-to-br from-primary-50 to-secondary-50 p-6 rounded-xl">
                        <div className="text-center">
                          <div className="text-4xl font-bold text-primary-600 mb-2">
                            {Math.round(user.results.overallPercentage)}%
                          </div>
                          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${getStatusColor(user.results.overallStatus)}`}>
                            {getStatusIcon(user.results.overallStatus)}
                            <span className="font-medium">{user.results.overallStatus}</span>
                          </div>
                          <div className="text-sm text-gray-600 mt-3">
                            Completed on {new Date(user.results.completedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Category Breakdown */}
                      {user.results.categories && (
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-3">Category Performance</h4>
                          <div className="space-y-3 max-h-64 overflow-y-auto">
                            {user.results.categories.map((category: any, index: number) => {
                              const categoryInfo = testData.find(c => c.id === category.categoryId);
                              return (
                                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-800 text-sm">
                                      {categoryInfo?.title || category.categoryId}
                                    </div>
                                    <div className="w-full bg-gray-200 h-2 rounded-full mt-2">
                                      <div 
                                        className="bg-primary-500 h-full rounded-full transition-all duration-500"
                                        style={{ width: `${category.percentage}%` }}
                                      />
                                    </div>
                                  </div>
                                  <div className="ml-4 text-right">
                                    <div className="font-bold text-primary-600">
                                      {Math.round(category.percentage)}%
                                    </div>
                                    <div className={`text-xs px-2 py-1 rounded-full ${getStatusColor(category.status)}`}>
                                      {category.status}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                      <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-600 mb-2">
                        Test Not Completed
                      </h4>
                      <p className="text-gray-500">
                        This user has not completed the export readiness test yet.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UserDetailsModal;